import { useMemo } from 'react';

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import type { FoxsyCampaignLeaderboardItem } from '@api';

import type { GridTableHeaderItem } from '@uikit/components/GridTable';

import { formatNumberLocalized } from '@shared/helpers/formatters';
import { concatCurrencyToAmount } from '@shared/helpers/web3/formatters';

import { Typography } from '@ui/uikit/components/common/Typography';

import { BaseCampaignLeaderBoard } from '../ui/BaseCampaignLeaderBoard/BaseCampaignLeaderBoard';

const getRankCellContent = (rank?: number): string => {
  switch (rank) {
    case 1:
      return '🥇 1st';
    case 2:
      return '🥈 2nd';
    case 3:
      return '🥉 3rd';
    default:
      return `🏆 ${rank}`;
  }
};

const columns: GridTableHeaderItem<FoxsyCampaignLeaderboardItem>[] = [
  {
    label: 'Rank',
    name: 'leaderboard_position',
    render: ({ item }) => {
      return getRankCellContent(item.leaderboard_position);
    },
  },
  {
    label: 'Trader',
    name: 'email',
    render: ({ item }) => (
      <Typography variant="caption-s" isEllipsis title={item.email ? item.email : ''}>
        {item.email}
      </Typography>
    ),
  },
  {
    label: 'Trading Volume',
    name: 'trading_volume',
    render: ({ item }) => (
      <Typography variant="caption-s" isEllipsis title={String(item.trading_volume)}>
        {concatCurrencyToAmount(formatNumberLocalized(item.trading_volume), '$')}
      </Typography>
    ),
  },
];

interface FoxsyCampaignLeaderBoardProps {
  query: UseInfiniteQueryResult<InfiniteData<any, unknown>, unknown>;
}

export const FoxsyCampaignLeaderBoard = ({ query }: FoxsyCampaignLeaderBoardProps) => {
  const currentUser = useMemo(() => query.data?.pages[0]?.user || {}, [query.data]);

  const leaderboard: FoxsyCampaignLeaderboardItem[] = useMemo(() => {
    const participants = query.data?.pages?.flatMap((page) => page.leaderboard) || [];

    if (currentUser && currentUser.user_id) {
      const hasSelfParticipant = !!participants.find(
        ({ user_id }: FoxsyCampaignLeaderboardItem) => user_id === currentUser.user_id,
      );

      if (!hasSelfParticipant) {
        participants.push(currentUser);
      }
    }

    return participants;
  }, [currentUser, query.data?.pages]);

  return (
    <>
      <BaseCampaignLeaderBoard
        items={leaderboard}
        columns={columns}
        query={query}
        currentUser={currentUser}
      />
      <div className="story-prose-none px-6 py-3 w-full text-right text-base-text-tertiary text-caption-lg-a font-semibold">
        The leaderboard updates every hour. If your volume isn’t reflected in real-time, don’t worry
        — it’s not an error.
      </div>
    </>
  );
};
